import Link from 'next/link';
import styled from 'styled-components';

import { ApiPresentation } from '~/api/v1/presentations/all_index/get';
import * as COLORS from '~/colors';
import { SusnetImage } from '~/components/_wrapper/SusnetImage';
import type { PresentationT } from '~/state/presentation';

type Props = {
  presentation: ApiPresentation | PresentationT;
  hideVoteComment?: boolean;
  isShowVote?: boolean;
  competitionId?: number;
};

export function CardPresentation({
  presentation,
  hideVoteComment,
  isShowVote,
  competitionId,
}: Props) {
  const { organization } = presentation;
  const color = COLORS.sdgs[organization.sdgs[0]] || { start: '#fff', end: '#fff' };

  return (
    <StyledWrapper
      href={`/presentations/${presentation.id}${
        competitionId ? `?competition-id=${competitionId}` : ''
      }`}
    >
      <StyledCardBody>
        <StyledCardBodyBgWhite />
        <StyledCardBodyBackground start={color.start} end={color.end} />
        <StyledCardBodyTop>
          <StyledCardBodyTopLabel>{organization.area}</StyledCardBodyTopLabel>
          <StyledCardBodyTopName>{organization.category}</StyledCardBodyTopName>
        </StyledCardBodyTop>
        <StyledCardBodyBottom>
          <StyledCardBodyBottomTitle>{organization.name}</StyledCardBodyBottomTitle>
          <StyledCardBodyBottomBorder />
          <StyledCardBodyBottomHashtagList>
            {organization.tags.map((tag, index) => (
              <StyledCardBodyBottomHashtagListItem key={index}>
                #{tag.name}
              </StyledCardBodyBottomHashtagListItem>
            ))}
          </StyledCardBodyBottomHashtagList>
        </StyledCardBodyBottom>
      </StyledCardBody>
      <StyledCardPresentation>
        <StyledCardPresentationThumbnail
          unoptimized
          src={presentation.thumbnail}
          alt={presentation.title}
          width={320}
          height={180}
          sizes='100vw'
        />
        <StyledCardPresentationBody>
          <StyledCardPresentationBodyTitle>{presentation.title}</StyledCardPresentationBodyTitle>
          {!hideVoteComment && (
            <StyledCardPresentationBodyTextBox>
              {isShowVote && (
                <StyledCardPresentationBodyTextVoted>
                  投票 {presentation.votes ?? 0} 人
                </StyledCardPresentationBodyTextVoted>
              )}
              <StyledCardPresentationBodyTextComment>
                コメント {presentation.comments ?? 0} 人
              </StyledCardPresentationBodyTextComment>
            </StyledCardPresentationBodyTextBox>
          )}
        </StyledCardPresentationBody>
      </StyledCardPresentation>
    </StyledWrapper>
  );
}

const StyledWrapper = styled(Link)`
  border-radius: 0 12px 12px 12px;

  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledCardBody = styled.div`
  position: relative;
  width: 100%;
`;

const StyledCardBodyBackground = styled.div<{ start: string; end: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, ${(props) => props.end}, ${(props) => props.start});
  opacity: 0.5;
  z-index: -1;
  border-radius: inherit;
`;

const StyledCardBodyBgWhite = styled.div`
  background: #fff;
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: -50;
`;

const StyledCardBodyTop = styled.div`
  display: flex;
  width: 100%;
`;

const StyledCardBodyTopLabel = styled.p`
  background-color: #000;
  color: #ffffff;
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  overflow: hidden;
  padding: 8px 12px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 105px;
  max-width: 150px;
`;

const StyledCardBodyTopName = styled.p`
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.3;
  overflow: hidden;
  padding: 8px 12px;
  text-align: left;
  width: 100%;
`;

const StyledCardBodyBottom = styled.div`
  padding: 16px 32px;
  width: 100%;
`;

const StyledCardBodyBottomTitle = styled.h3`
  display: -webkit-box;
  font-size: 20px;
  font-weight: bold;
  min-height: 90px;
  line-height: 1.5;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  width: 100%;
  word-wrap: break-word;
`;

const StyledCardBodyBottomBorder = styled.hr`
  border-top: 1px solid #070707;
  margin: 16px 0;
  width: 100%;
`;

const StyledCardBodyBottomHashtagList = styled.ul`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  min-height: 36px;
  list-style: none;
  width: 100%;
`;

const StyledCardBodyBottomHashtagListItem = styled.li`
  color: #070707;
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.5;
  margin-right: 16px;
`;

const StyledCardPresentation = styled.div`
  background-color: #fff;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledCardPresentationThumbnail = styled(SusnetImage)`
  object-fit: cover;
  width: 320px;
  height: 180px;
`;

const StyledCardPresentationBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 32px;
  width: 100%;
`;

const StyledCardPresentationBodyTitle = styled.p`
  display: -webkit-box;
  font-size: 16px;
  font-weight: bold;
  min-height: 72px;
  line-height: 1.5;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  width: 100%;
  word-wrap: break-word;
`;

const StyledCardPresentationBodyTextBox = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledCardPresentationBodyTextVoted = styled.p`
  color: #6c6c6c;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.3;
`;

const StyledCardPresentationBodyTextComment = styled.p`
  color: #6c6c6c;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.3;
`;
